/**
 * Browser push registration.
 *
 * The previous version shipped a complete-looking Firebase integration that was
 * never imported, whose service worker was never registered, and whose token was
 * never sent anywhere — so on a crash-alert product, push silently did nothing.
 * This module is small on purpose and every step is observable.
 *
 * Configuration comes from VITE_FIREBASE_* at build time. When it is absent the
 * feature reports itself unavailable rather than half-working.
 *
 * Backend limitation worth knowing: `users.push_token` is a single column, so a
 * user has exactly one registered browser. Signing in elsewhere silently replaces
 * it. The UI says so; fixing it properly needs a device_tokens table.
 */

import { firebaseConfig, firebaseVapidKey, isPushConfigured } from './env';

export type PushSupport = 'ready' | 'unconfigured' | 'unsupported' | 'denied';

export function pushSupport(): PushSupport {
  if (!isPushConfigured) return 'unconfigured';
  if (typeof window === 'undefined') return 'unsupported';
  if (!('Notification' in window) || !('serviceWorker' in navigator)) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  return 'ready';
}

/**
 * Requests permission, registers the worker, and returns an FCM token. Throws with
 * a message worth showing rather than resolving to null, so callers cannot mistake
 * failure for success — that confusion is what made the old implementation useless.
 */
export async function registerForPush(): Promise<string> {
  if (!isPushConfigured) {
    throw new Error('Push notifications are not configured for this deployment.');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Notification permission was not granted.');
  }

  // Imported lazily so the Firebase SDK stays out of the main bundle for the many
  // deployments that never enable push.
  const [{ initializeApp }, { getMessaging, getToken, isSupported }] = await Promise.all([
    import('firebase/app'),
    import('firebase/messaging'),
  ]);

  if (!(await isSupported())) {
    throw new Error('This browser cannot receive push notifications.');
  }

  const registration = await navigator.serviceWorker.register(
    `/firebase-messaging-sw.js?config=${encodeURIComponent(JSON.stringify(firebaseConfig))}`,
    { scope: '/' }
  );

  const app = initializeApp(firebaseConfig as Record<string, string>);
  const token = await getToken(getMessaging(app), {
    vapidKey: firebaseVapidKey,
    serviceWorkerRegistration: registration,
  });

  if (!token) throw new Error('The browser did not return a notification token.');
  return token;
}
